import { ReservationType} from "./common";
import { HotelMessageLevel } from "./constants";
import { ProviderLabel, ProviderType } from "./providers";

export const providerConfig = {
  [ProviderType.oklo]: {
    // Display Configuration
    label: ProviderLabel.oklo,
    chipColor: 'success',

    // Form Validation
    requiresGuestInfo: false,
    requiresCancellation: false,
    guestFieldsRequired: false,

    // Room Configuration
    useBaseRoomTypeId: false, // Uses base RoomTypeId without modifications
    roomIdSeparator: null,

    allowMultipleHotels: true,
    allowSingleHotels: false,

    allowMixedItems: true,
    allowGetConfirmationNumber: false,
    hotelMessageLevel: HotelMessageLevel.roomLevel,
    allowedLeadGuestTypes: ['Adult'], // Only adults can be lead guests
  },

  [ProviderType.tbo]: {
    // Display Configuration
    label: ProviderLabel.tbo,
    chipColor: 'primary',

    // Form Validation
    requiresGuestInfo: true,
    requiresCancellation: false,
    guestFieldsRequired: true,

    // Room Configuration
    useBaseRoomTypeId: false,
    roomIdSeparator: null,

    allowMultipleHotels: false,
    allowSingleHotels: false,
    allowGetConfirmationNumber: false,

  },

  [ProviderType.illusions]: {
    // Display Configuration
    label: ProviderLabel.illusions,
    chipColor: 'warning',

    // Form Validation
    requiresGuestInfo: true,
    requiresCancellation: true,
    guestFieldsRequired: true,

    // Room Configuration
    useBaseRoomTypeId: true, // Uses RoomTypeId with MealPlanId
    roomIdSeparator: '-',

    allowMultipleHotels: false,
    allowSingleHotels: true,

    allowMixedItems: false,
    allowGetConfirmationNumber: true,
    hotelMessageLevel: HotelMessageLevel.hotelLevel,
    allowedLeadGuestTypes: ['Adult'], // Only adults can be lead guests
  },

  [ProviderType.unknown]: {
    // Display Configuration
    label: ProviderLabel.unknown,
    chipColor: 'warning',
    hotelMessageLevel: HotelMessageLevel.hotelLevel,
  }
};

export const getProviderConfig = (provider) => {
  if (!provider) {
    return providerConfig[ProviderType.unknown];
  }

  if (!providerConfig[provider]) {
    return providerConfig[ProviderType.unknown];
  }

  return providerConfig[provider];
};

export const providerHelpers = {
  // Form validation
  requiresGuestInfo: (provider) =>
    getProviderConfig(provider).requiresGuestInfo,

  requiresCancellation: (provider) =>
    getProviderConfig(provider).requiresCancellation,

  fieldsRequired: (provider) =>
    getProviderConfig(provider).guestFieldsRequired,

  // Check the provider use base RoomTypeId or RoomTypeId + MealPlanId
  getRoomTypeId: (provider, room) => {
    const config = getProviderConfig(provider);

    if (!config.useBaseRoomTypeId) {
      return room.RoomTypeId;
    }

    return room.Metadata.Illusions.MealPlanId
      ? room.Metadata.Illusions.MealPlanId
      : room.RoomTypeId;
  },

  getRoomName: (provider, room) => {
    const config = getProviderConfig(provider);

    if (!config.useBaseRoomTypeId) {
      return room.Name;
    }

    return room.Metadata.Illusions.MealPlanName
      ? `${room.Name} ${config.roomIdSeparator} ${room.Metadata.Illusions.MealPlanName}`
      : room.Name;
  },

  shouldShowOnlySingleHotel: (provider) => {
    const config = getProviderConfig(provider);
    return config.allowSingleHotels || false;
  },

  allowsMixedItems: (provider) => {
    const config = getProviderConfig(provider);
    return config.allowMixedItems || false;
  },

  requiresCartClear: (currentProvider, newProvider) => {
    return currentProvider && currentProvider !== newProvider;
  },

  shouldShowConfirmation: (
    currentProvider,
    newProvider,
    newItemType,
    hasHotels,
    hasServices,
    newItemId,
    currentItems
  ) => {
    // Empty cart - no confirmation
    if (!currentProvider || (!hasHotels && !hasServices)) {
      return false;
    }

    // Different provider - always confirm
    if (currentProvider !== newProvider) {
      return true;
    }

    // Same provider - check if adding the same item
    if (currentProvider === newProvider && newItemType === ReservationType.hotel) {
      const isSameHotel = currentItems.some(
        hotel => hotel.PropertyId === newItemId
      );

      if (isSameHotel) {
        return false;
      }

      // Same provider - check provider wise allows single hotel/ multiple hotels
      return providerHelpers.shouldShowOnlySingleHotel(newProvider);
    }

    if (currentProvider === newProvider && newItemType === ReservationType.service) {
      return !providerHelpers.allowsMixedItems(newProvider);
    }

    return false;
  },

  getConfirmationNumber: (provider) => {
    const config = getProviderConfig(provider);
    return config.allowGetConfirmationNumber || false;
  },

  getHotelMessageLevel: (provider) => {
    const config = getProviderConfig(provider);
    return config.hotelMessageLevel || HotelMessageLevel.hotelLevel;
  },

  canBeLeadGuest: (provider, paxType) => {
    const config = getProviderConfig(provider);
    if (!config.allowedLeadGuestTypes) {
      return true;
    }
    return config.allowedLeadGuestTypes.includes(paxType);
  },

  extractHotelMessages: (provider, data) => {
    const messageLevel = providerHelpers.getHotelMessageLevel(provider);
    
    if (messageLevel === HotelMessageLevel.roomLevel) {
      const messages = new Set();
      data?.RoomRequestResults?.forEach(request => {
        request.Rooms?.forEach(room => {
          room.HotelRestrictions?.forEach(restriction => {
                    const messageWithRoomType = {
                        ...restriction,
                        RoomTypeId: room.RoomTypeId
                    };
            messages.add(JSON.stringify(messageWithRoomType));
          });
        });
      });
      return Array.from(messages).map(msg => JSON.parse(msg));
    }
    
    return data?.HotelRestrictions || [];
  },
};

export const transformRoomByProvider = (provider, room) => {
  const config = getProviderConfig(provider);

  const baseRoom = {
    ...room,
    BaseRoomTypeId: room.RoomTypeId,
  };

  if (config.useBaseRoomTypeId) {
    return {
      ...baseRoom,
      RoomTypeId: providerHelpers.getRoomTypeId(provider, room),
      Name: providerHelpers.getRoomName(provider, room),
    };
  }

  return baseRoom;
};

export const useProviderConfig = (provider) => {
  return {
    config: getProviderConfig(provider),
    helpers: {
      requiresGuestInfo: () => providerHelpers.requiresGuestInfo(provider),
      fieldsRequired: () => providerHelpers.fieldsRequired(provider),
      getRoomTypeId: (room) => providerHelpers.getRoomTypeId(provider, room),
      getRoomName: (room) => providerHelpers.getRoomName(provider, room),
      getConfirmationNumber: () => providerHelpers.getConfirmationNumber(provider),
      getHotelMessageLevel: () => providerHelpers.getHotelMessageLevel(provider),
      extractHotelMessages: (data) => providerHelpers.extractHotelMessages(provider, data),
      shouldShowConfirmation: (
        currentProvider,
        newItemType,
        hasHotels,
        hasServices,
        newItemId,
        currentItems
      ) =>
        providerHelpers.shouldShowConfirmation(
          currentProvider,
          provider,
          newItemType,
          hasHotels,
          hasServices,
          newItemId,
          currentItems
        ),
      canBeLeadGuest: (paxType) => providerHelpers.canBeLeadGuest(provider, paxType),
    },
  };
};

export default providerConfig;